// apps/client/src/components/loans/LoanSearchBar.tsx
// Loan search bar with dark theme styling

import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { DateRangePicker } from '../common/DateRangePicker';
import { LoanListFilters, LoanListStatusFilter } from '../../hooks/useLoanList';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';

const STATUS_LABELS: Record<LoanListStatusFilter, string> = {
  all: 'All Loans',
  active: 'Active',
  discharged: 'Discharged',
  trail_stopped: 'Trail Stopped',
};

interface LoanSearchBarProps {
  filters: LoanListFilters;
  onFiltersChange: (filters: Partial<LoanListFilters>) => void;
  onClearFilters: () => void;
  /** Offer the Trail Stopped status. The server refuses it for callers who may not see trail. */
  showTrailStopped?: boolean;
}

export function LoanSearchBar({
  filters,
  onFiltersChange,
  onClearFilters,
  showTrailStopped = false,
}: LoanSearchBarProps) {
  const { companyId } = useAuth();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [brokers, setBrokers] = useState<string[]>([]);
  const [lenders, setLenders] = useState<string[]>([]);
  const [loadingBrokers, setLoadingBrokers] = useState(true);
  const [loadingLenders, setLoadingLenders] = useState(true);

  // Load brokers from BigQuery (actual broker_display values from commission data)
  // Different suffixes like "(TRL PUR)" represent different loan books
  // v2: Now using getDistinctBrokers endpoint
  useEffect(() => {
    if (!companyId) return;

    const loadBrokers = async () => {
      try {
        setLoadingBrokers(true);
        const getDistinctBrokers = httpsCallable<
          { companyId: string; officeIds?: string[] },
          string[]
        >(functions, 'getDistinctBrokers');

        // Let the backend resolveOfficeScope decide office scoping based on role.
        // Sending officeIds from the frontend caused a mismatch: the frontend checked
        // only canSeeAllOffices (claim), while the backend also checks role.
        const result = await getDistinctBrokers({ companyId });
        setBrokers(result.data || []);
      } catch (error) {
        console.error('Error loading brokers:', error);
        setBrokers([]);
      } finally {
        setLoadingBrokers(false);
      }
    };

    loadBrokers();
  }, [companyId]);

  // Load lenders dynamically from BigQuery
  // Re-fetch when broker changes to show only lenders for that broker
  useEffect(() => {
    if (!companyId) return;

    const loadLenders = async () => {
      try {
        setLoadingLenders(true);
        const getDistinctLenders = httpsCallable<
          { companyId: string; brokerName?: string },
          string[]
        >(functions, 'getDistinctLenders');

        // Pass broker filter to get contextual lenders
        const result = await getDistinctLenders({
          companyId,
          brokerName: filters.brokerName,
        });
        const newLenders = result.data || [];
        setLenders(newLenders);

        // Clear lender filter if selected lender isn't available for new broker
        if (filters.lender && !newLenders.includes(filters.lender)) {
          onFiltersChange({ lender: undefined });
        }
      } catch (error) {
        console.error('Error loading lenders:', error);
        // Fallback to common lenders if the endpoint fails
        setLenders([
          'ANZ',
          'CBA',
          'NAB',
          'Westpac',
          'Macquarie',
          'Bankwest',
          'Suncorp',
          'ING',
          'AMP',
          'Pepper Money',
          'Liberty Financial',
          'La Trobe Financial',
        ]);
      } finally {
        setLoadingLenders(false);
      }
    };

    loadLenders();
    // Note: filters.lender and onFiltersChange intentionally excluded to avoid infinite loops
    // We only want to re-fetch when broker changes, not when lender is cleared
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filters.brokerName]);

  const hasActiveFilters =
    filters.search ||
    filters.brokerName ||
    filters.lender ||
    filters.settlementDateStart ||
    filters.settlementDateEnd ||
    filters.loanAmountMin !== undefined ||
    filters.loanAmountMax !== undefined ||
    (filters.status && filters.status !== 'all');

  return (
    <div>
      {/* Main search bar */}
      <div className="flex gap-3 items-center">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by customer name or loan ID..."
            value={filters.search || ''}
            onChange={(e) => onFiltersChange({ search: e.target.value })}
            className="block w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm"
          />
        </div>

        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            showAdvanced || hasActiveFilters
              ? 'bg-blue-600 border-blue-500 text-white'
              : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
          }`}
        >
          <FunnelIcon className="h-4 w-4" />
          Filters
          {hasActiveFilters && !showAdvanced && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white">
              Active
            </span>
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <XMarkIcon className="h-4 w-4" />
            Clear
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 mt-3">
          {filters.search && (
            <FilterChip
              label={`Search: "${filters.search}"`}
              onRemove={() => onFiltersChange({ search: undefined })}
            />
          )}
          {filters.brokerName && (
            <FilterChip
              label={`Broker: ${filters.brokerName}`}
              onRemove={() => onFiltersChange({ brokerName: undefined })}
            />
          )}
          {filters.lender && (
            <FilterChip
              label={`Lender: ${filters.lender}`}
              onRemove={() => onFiltersChange({ lender: undefined })}
            />
          )}
          {(filters.settlementDateStart || filters.settlementDateEnd) && (
            <FilterChip
              label={`Settlement: ${filters.settlementDateStart || '...'} to ${filters.settlementDateEnd || '...'}`}
              onRemove={() =>
                onFiltersChange({ settlementDateStart: undefined, settlementDateEnd: undefined })
              }
            />
          )}
          {(filters.loanAmountMin !== undefined || filters.loanAmountMax !== undefined) && (
            <FilterChip
              label={`Amount: ${filters.loanAmountMin ? formatMoney(filters.loanAmountMin) : '$0'} - ${filters.loanAmountMax ? formatMoney(filters.loanAmountMax) : 'No limit'}`}
              onRemove={() => onFiltersChange({ loanAmountMin: undefined, loanAmountMax: undefined })}
            />
          )}
          {filters.status && filters.status !== 'all' && (
            <FilterChip
              label={`Status: ${STATUS_LABELS[filters.status]}`}
              onRemove={() => onFiltersChange({ status: 'all' })}
            />
          )}
        </div>
      )}

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Broker filter */}
          <div>
            <label htmlFor="broker" className="block text-sm font-medium text-gray-300 mb-2">
              Broker
            </label>
            <select
              id="broker"
              value={filters.brokerName || ''}
              onChange={(e) => onFiltersChange({ brokerName: e.target.value || undefined })}
              disabled={loadingBrokers}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="">All Brokers</option>
              {brokers.map((broker) => (
                <option key={broker} value={broker}>
                  {broker}
                </option>
              ))}
            </select>
          </div>

          {/* Lender filter */}
          <div>
            <label htmlFor="lender" className="block text-sm font-medium text-gray-300 mb-2">
              Lender
            </label>
            <select
              id="lender"
              value={filters.lender || ''}
              onChange={(e) => onFiltersChange({ lender: e.target.value || undefined })}
              disabled={loadingLenders}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="">All Lenders</option>
              {lenders.map((lender) => (
                <option key={lender} value={lender}>
                  {lender}
                </option>
              ))}
            </select>
          </div>

          {/* Status filter */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-2">
              Status
            </label>
            <select
              id="status"
              value={filters.status || 'all'}
              onChange={(e) => onFiltersChange({ status: e.target.value as LoanListStatusFilter })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Loans</option>
              <option value="active">Active Only</option>
              <option value="discharged">Discharged Only</option>
              {showTrailStopped && <option value="trail_stopped">Trail Stopped</option>}
            </select>
          </div>

          {/* Settlement date range */}
          <DateRangePicker
            startDate={filters.settlementDateStart}
            endDate={filters.settlementDateEnd}
            onRangeChange={(start, end) =>
              onFiltersChange({
                settlementDateStart: start,
                settlementDateEnd: end,
              })
            }
            label="Settlement Date Range"
          />

          {/* Loan amount range */}
          <div>
            <label htmlFor="loanAmountMin" className="block text-sm font-medium text-gray-300 mb-2">
              Min Loan Amount
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="loanAmountMin"
                value={filters.loanAmountMin !== undefined ? filters.loanAmountMin / 100 : ''}
                onChange={(e) =>
                  onFiltersChange({
                    loanAmountMin: e.target.value ? parseFloat(e.target.value) * 100 : undefined,
                  })
                }
                placeholder="0"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-7 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label htmlFor="loanAmountMax" className="block text-sm font-medium text-gray-300 mb-2">
              Max Loan Amount
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="loanAmountMax"
                value={filters.loanAmountMax !== undefined ? filters.loanAmountMax / 100 : ''}
                onChange={(e) =>
                  onFiltersChange({
                    loanAmountMax: e.target.value ? parseFloat(e.target.value) * 100 : undefined,
                  })
                }
                placeholder="No limit"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-7 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for removable filter chips
function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-700 border border-gray-600 rounded-full text-sm text-gray-200">
      {label}
      <button
        onClick={onRemove}
        className="p-0.5 rounded-full hover:bg-gray-600 focus:outline-none"
        aria-label={`Remove filter: ${label}`}
      >
        <XMarkIcon className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

// Helper to format money from cents
function formatMoney(cents: number): string {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
